/**
 * src/pages/marketing/RenFestSelfieAdmin.jsx
 * ═══════════════════════════════════════════════════════════════════════════
 * THE LEDGER — the operator's back room of the RenFest booth.
 *
 * ⚠️ ADMIN ONLY, AND THE SERVER IS THE GATE, NOT THIS FILE.
 * `renfest-selfie-status` refuses to send `runs`, `stats` or `volume` to
 * anyone whose `user_roles.role` is not `admin`, and refuses `q=sources`
 * outright. Hiding the button is presentation; the refusal is the security.
 * Never add a client-side role check and call it done.
 *
 * ⚠️ SOURCE FRAMES ARE FETCHED PER ROW, ON DEMAND.
 * The reference photographs are raw selfies of identifiable people. Signing
 * them for every row on every refresh would be dozens of Storage round trips
 * AND would put a wall of strangers' faces on screen whether anyone asked or
 * not. Open a row, and only then does that row's evidence get signed — with a
 * 10-minute TTL, not the hour a guest's own portrait gets.
 *
 * What this screen is for: seeing the work arrive during an event, and being
 * able to answer "what did that one cost, how long did it take, and what did it
 * have to work with" for any individual portrait without opening the database.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RotateCcw, AlertTriangle, Sparkles, ChevronRight, ChevronDown, Settings } from 'lucide-react';
import { api } from '../../lib/api';
import { GOLD, buzz } from '../../components/features/renfest/boothChrome';
import { loadBoothSettings, saveBoothSettings } from '../../components/features/renfest/boothSettings';
import {
  vibeProbe, say, unlockCues, cueReport, hasVibrate, hasSpeech,
} from '../../components/features/renfest/boothCues';

const secs = (ms) => (ms == null ? '-' : `${(ms / 1000).toFixed(1)}s`);
const usd = (n) => (n == null ? '-' : `$${Number(n).toFixed(4)}`);
const kb = (n) => (n == null ? '-' : `${Math.round(n / 1024)} KB`);

/**
 * Volume bars, drawn in CSS.
 *
 * ⚠️ Deliberately NOT recharts. The chart library already lives in its own
 * 503 KB async chunk; pulling it into the booth bundle to draw twenty-four
 * rectangles would be the heaviest thing on a page that has to load on a
 * festival hotspot. Divs scale fine.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CUE TEST — does this actual phone buzz, and does it talk?
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHY THIS PANEL EXISTS. "The vibration does not work" turned out to have at
 * least five unrelated causes, and none of them are distinguishable from the
 * code:
 *
 *   1. no Vibration API at all ................ iOS, permanently
 *   2. pulse too short to feel ................ the API returns TRUE and the
 *                                               hardware does move; a finger
 *                                               simply cannot register ~10ms
 *   3. phone in Silent or Do Not Disturb ...... OS suppresses it, silently
 *   4. system vibration/haptics switched off .. same, and just as invisible
 *   5. no sticky user activation .............. browser drops the call
 *
 * navigator.vibrate() returns true in cases 2, 3 and 4. There is no API that
 * reports the ringer switch, the haptics setting, or whether a human felt
 * anything. So the only instrument that can tell these apart is a person with
 * the phone in their hand, and this panel is that instrument.
 *
 * ⚠️ THE LADDER IS THE POINT. Tap down the durations and note the first one
 * you can feel — that number replaces VIBE_FLOOR in boothCues.jsx with a
 * measurement instead of my guess. If NONE of them are felt but they all
 * return true, it is the phone's settings, not the booth.
 */
const VIBE_LADDER = [10, 25, 50, 100, 200, 400];

function CueTest() {
  const [lines, setLines] = useState([]);
  const push = (s) => setLines((l) => [s, ...l].slice(0, 10));

  const env = cueReport();

  return (
    <div className="rf-wood p-5">
      <h2 className="rf-ink text-2xl mb-1" style={{ color: GOLD }}>Does this phone buzz?</h2>
      <p className="rf-ink mb-3" style={{ color: 'rgba(247,228,187,.62)', fontSize: '.82rem' }}>
        Tap down the ladder and note the shortest one you can actually feel. If every one
        of them says <em>accepted</em> and you feel nothing, the phone is suppressing it —
        check Silent mode, Do Not Disturb, and Settings &rarr; Sound &amp; vibration.
      </p>

      <div className="rf-chips mb-3">
        {VIBE_LADDER.map((ms) => (
          <button
            key={ms} type="button" className="rf-chip"
            onClick={() => {
              const r = vibeProbe(ms);
              push(`${ms}ms - ${r.returned === true ? 'accepted' : `returned ${String(r.returned)}`}`
                + `${r.activated === false ? ' - NO USER ACTIVATION' : ''}`);
            }}
          >
            {ms}ms
          </button>
        ))}
      </div>

      <div className="rf-chips mb-3">
        <button type="button" className="rf-chip" onClick={() => {
          const r = vibeProbe(0);
          push(`pattern - ${r.returned === true ? 'accepted' : String(r.returned)}`);
          try { navigator.vibrate?.([120, 90, 120, 90, 250]); } catch { /* reported above */ }
        }}>
          Long pattern
        </button>
        <button type="button" className="rf-chip" onClick={() => {
          unlockCues();
          const ok = say('Three. Two. One.');
          push(`speech - ${ok ? 'sent to synthesiser' : 'refused'} - turn the ringer up if silent`);
        }}>
          Say &ldquo;3, 2, 1&rdquo;
        </button>
      </div>

      <div className="rf-ink" style={{ color: 'rgba(247,228,187,.75)', fontSize: '.82rem', lineHeight: 1.6 }}>
        <div>Vibration API: <strong style={{ color: hasVibrate ? '#8ee08e' : '#ff8a8a' }}>{hasVibrate ? 'present' : 'absent'}</strong></div>
        <div>Speech API: <strong style={{ color: hasSpeech ? '#8ee08e' : '#ff8a8a' }}>{hasSpeech ? 'present' : 'absent'}</strong></div>
        <div>User activation: <strong>{String(env.activated)}</strong> &middot; current floor: {env.vibe_floor}ms</div>
      </div>

      {lines.length > 0 && (
        <ul className="mt-3" style={{ color: 'rgba(247,228,187,.9)', fontSize: '.8rem', fontFamily: 'monospace' }}>
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
    </div>
  );
}

function VolumeBars({ rows, mode }) {
  const max = Math.max(1, ...rows.map((r) => r.runs));
  const fmt = (iso) => {
    const d = new Date(iso);
    return mode === 'hourly'
      ? d.toLocaleTimeString([], { hour: 'numeric' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!rows.length) {
    return <p className="rf-ink" style={{ color: 'rgba(247,228,187,.5)' }}>No runs in this window yet.</p>;
  }

  return (
    <div className="flex items-end gap-1 overflow-x-auto rf-scroll" style={{ height: 150 }}>
      {rows.map((r) => {
        const h = Math.max(3, Math.round((r.runs / max) * 118));
        const failRatio = r.runs ? r.failed / r.runs : 0;
        return (
          <div key={r.bucket} className="shrink-0 flex flex-col items-center justify-end" style={{ width: 30, height: '100%' }}>
            <span className="rf-ink" style={{ color: GOLD, fontSize: '.72rem' }}>{r.runs}</span>
            <div
              title={`${r.runs} runs · ${r.ok} ok · ${r.failed} failed · ${usd(r.cost_usd)} · avg ${secs(r.avg_ms)} · ${r.users} people`}
              style={{
                width: 20, height: h, borderRadius: '4px 4px 0 0',
                // Failures colour the bar rather than adding a second series —
                // at this size a stacked chart is unreadable and a tinted bar
                // still answers "was that hour healthy?" at a glance.
                background: failRatio > 0.25
                  ? 'linear-gradient(180deg,#e06a4a,#8a3a22)'
                  : 'linear-gradient(180deg,#f7cf74,#c9891f)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.45)',
              }}
            />
            <span className="rf-ink" style={{ color: 'rgba(247,228,187,.5)', fontSize: '.62rem', marginTop: 2 }}>
              {fmt(r.bucket)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ component ═══ */

export default function RenFestSelfieAdmin({ onLocked }) {
  const [log, setLog] = useState({ runs: [], stats: [], totals: null, volume: { hourly: [], daily: [] } });
  const [mode, setMode] = useState('hourly');
  const [openRow, setOpenRow] = useState(null);
  const [sources, setSources] = useState({});     // job_id -> { sources, partials }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [settings, setSettings] = useState(loadBoothSettings);
  const [savedNote, setSavedNote] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { setLog(await api.renfestSelfie.runs(100)); }
    catch (e) {
      /* ⚠️ A REFUSED KEY MUST TAKE THE UI BACK TO THE LOCK SCREEN.
         api.js already discards a rejected operator key, so without this the
         Ledger sits there looking unlocked and failing every refresh for as
         long as anyone leaves it open - which at an event is all day. */
      if (e?.status === 401 || e?.status === 403) onLocked?.();
      setErr(String(e.message || e));
    }
    setBusy(false);
  }, [onLocked]);

  useEffect(() => { load(); }, [load]);

  /* ⚠️ THE LEDGER REFRESHES ITSELF EVERY 30s.
     At an event nobody is going to sit tapping Refresh, and a failure nobody
     sees for an hour is a failure that ran for an hour. This is the cheapest
     possible version of "know within minutes". */
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    const id = setInterval(() => loadRef.current?.(), 30000);
    return () => clearInterval(id);
  }, []);

  async function toggleRow(jobId) {
    buzz(8);
    if (openRow === jobId) { setOpenRow(null); return; }
    setOpenRow(jobId);
    if (sources[jobId]) return;
    try {
      const data = await api.renfestSelfie.sources(jobId);
      setSources((m) => ({ ...m, [jobId]: data }));
    } catch (e) {
      setSources((m) => ({ ...m, [jobId]: { error: String(e.message || e) } }));
    }
  }

  // The views come back newest-first (cheap index scan); a chart reads
  // oldest-left. The mode pick happens INSIDE the memo on purpose - computing
  // it outside creates a fresh array identity on every render, which makes the
  // dependency useless and reverses the list every single time.
  const ordered = useMemo(() => {
    const rows = mode === 'hourly' ? log.volume?.hourly : log.volume?.daily;
    return [...(rows || [])].reverse();
  }, [mode, log.volume]);

  function setSetting(patch) {
    buzz(8);
    const next = { ...settings, ...patch };
    setSettings(next);
    saveBoothSettings(next);
    setSavedNote('Saved for this device');
    setTimeout(() => setSavedNote(''), 2000);
  }

  /* Failures first, newest first. `stage` is what makes these actionable:
     client_camera never reached us, client_upload died on the way, provider
     means the model refused. Three different fixes. */
  const problems = useMemo(
    () => (log.runs || []).filter((r) => r.status === 'error').slice(0, 8),
    [log.runs],
  );

  const th = 'text-left rf-ink py-2 pr-3 whitespace-nowrap';
  const td = 'py-2 pr-3 whitespace-nowrap';
  const dim = { color: 'rgba(247,228,187,.72)' };

  return (
    <div className="space-y-4">
      <div className="rf-wood p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="rf-ink" style={{ fontSize: 'clamp(1.8rem, 7vw, 2.6rem)', lineHeight: 1, color: GOLD }}>
              The Ledger
            </h1>
            <p className="rf-ink mt-1" style={{ color: 'rgba(247,228,187,.7)' }}>
              Every portrait, what it cost and what it was painted from
            </p>
          </div>
          <button type="button" onClick={() => { buzz(8); load(); }} className="rf-chip">
            <RotateCcw size={16} className="inline mr-1.5" />{busy ? 'Reading...' : 'Refresh'}
          </button>
        </div>

        {log.totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {[
              ['Portraits', log.totals.runs],
              ['Finished', log.totals.ok],
              ['Failed', log.totals.failed],
              ['Spend', `$${Number(log.totals.cost || 0).toFixed(2)}`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl px-3 py-2" style={{ background: 'rgba(0,0,0,.28)', boxShadow: 'inset 0 2px 8px rgba(0,0,0,.4)' }}>
                <span className="rf-ink block" style={{ color: 'rgba(247,228,187,.6)', fontSize: '.78rem' }}>{k}</span>
                <span className="rf-ink" style={{ color: GOLD, fontSize: '1.6rem' }}>{v}</span>
              </div>
            ))}
          </div>
        )}

        {err && (
          <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-3 flex gap-2">
            <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-xs text-red-400 break-words">{err}</span>
          </div>
        )}
      </div>

      {/* ── problems, first and loudest ───────────────────────────
          ⚠️ THIS SITS ABOVE EVERYTHING ELSE ON PURPOSE. At a festival, a booth
          that has quietly been failing for an hour is the worst outcome there
          is - the operator is sweating, the queue is moving, and nobody is
          reading a table. Failures get their own red panel at the top, with
          the STAGE, because stage is what tells you which fix to reach for:
            client_camera  - never left the phone; permission or a dead stream
            client_capture - camera returned empty frames
            client_upload  - died on the way to Storage; usually the network
            client_post    - the generator refused it
            provider       - the model failed; check the attempts column
            storage        - we could not read or write the bucket */}
      {problems.length > 0 && (
        <div className="rounded-3xl p-5" style={{
          background: 'linear-gradient(180deg, rgba(120,32,18,.55), rgba(60,16,10,.55))',
          border: '1px solid rgba(224,106,74,.5)',
          boxShadow: '0 10px 28px rgba(0,0,0,.3)',
        }}>
          <h2 className="rf-ink text-2xl mb-2" style={{ color: '#ffb59a' }}>
            <AlertTriangle size={20} className="inline mr-2" />
            {problems.length} recent problem{problems.length === 1 ? '' : 's'}
          </h2>
          <div className="space-y-2">
            {problems.map((r) => (
              <div key={r.job_id} className="rounded-xl px-3 py-2" style={{ background: 'rgba(0,0,0,.3)' }}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="rf-ink" style={{ color: '#ffb59a', fontSize: '1rem' }}>
                    {r.stage || 'provider'}
                  </span>
                  <span className="rf-ink" style={{ color: 'rgba(247,228,187,.55)', fontSize: '.8rem' }}>
                    {new Date(r.created_at).toLocaleTimeString()} &middot; {r.user_email || 'unknown'}
                  </span>
                  <button type="button" onClick={() => toggleRow(r.job_id)}
                          className="ml-auto rf-ink" style={{ color: GOLD, fontSize: '.8rem' }}>
                    details
                  </button>
                </div>
                <p className="mt-1 break-words" style={{ color: '#f7d9cc', fontSize: '.86rem' }}>{r.error}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── operator settings ─────────────────────────────────────
          ⚠️ THESE USED TO BE IN FRONT OF GUESTS AND THAT WAS THE BUG.
          DESIGN-FUNDAMENTALS A7: if a choice needs explaining, it is not a
          guest choice. Style, banner and prompt text all fail that test in a
          hot, noisy crowd - so they live here, where whoever is running the
          booth sets them once for the whole event.

          Stored per DEVICE, same as the inventory TV boards: a booth is a
          physical machine on a counter and its operator configures THAT
          machine. When this becomes the platform, this panel is the seam -
          these values start coming from the event's config row instead. */}
      <div className="rf-wood p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="rf-ink text-2xl" style={{ color: GOLD }}>
            <Settings size={20} className="inline mr-2" />Booth settings
          </h2>
          {savedNote && (
            <span className="rf-ink ml-auto" style={{ color: 'rgba(247,228,187,.6)', fontSize: '.85rem' }}>{savedNote}</span>
          )}
        </div>
        <p className="rf-ink mb-3" style={{ color: 'rgba(247,228,187,.6)', fontSize: '.85rem' }}>
          Guests never see these. Set them once for the event.
        </p>

        <div className="rf-field">
          <span className="rf-label">Style</span>
          <div className="rf-chips">
            {/* ⚠️ SAME WORDS THE GUEST SEES, on purpose. This screen is open at the
                booth in public; an operator saying "caricature" out loud is the
                exact vocabulary the festival objected to on 2026-08-31. Order
                matches STYLES in RenFestSelfie.jsx. */}
            {[['light', 'Flattering'], ['true', 'Realistic'], ['full', 'Cartoony']].map(([v, l]) => (
              <button key={v} type="button" className={`rf-chip${settings.likeness === v ? ' is-on' : ''}`}
                      onClick={() => setSetting({ likeness: v })}>{l}</button>
            ))}
          </div>
        </div>

        {/* ⚠️ The Painter row used to live here. It does not any more, and it is not
            coming back as a client control. Which model paints, and at what quality,
            is a platform decision made per account - it is our money under
            `platform_key` and the client's under `own_key` (`D-41`), and a booth
            operator picking `openai2-hi` on a busy Saturday changes the bill and the
            queue for everyone. The server now resolves the painter from
            `event.model_primary` -> `tenant.model_primary` -> the built-in, and
            honours a painter named on the request only for a platform admin
            (`D-43`). Leaving the chips here would have let an operator set something
            the server then silently ignored, which is worse than no control at all. */}

        <div className="rf-field">
          <span className="rf-label">Banner</span>
          <div className="rf-chips">
            {[[true, 'KC RENFEST 2026'], [false, 'No lettering']].map(([v, l]) => (
              <button key={String(v)} type="button" className={`rf-chip${settings.banner === v ? ' is-on' : ''}`}
                      onClick={() => setSetting({ banner: v })}>{l}</button>
            ))}
          </div>
        </div>

        <div className="rf-field">
          <span className="rf-label">Embellishment</span>
          <input
            className="rf-input"
            style={{ maxWidth: 420 }}
            value={settings.extra}
            onChange={(e) => setSettings({ ...settings, extra: e.target.value })}
            onBlur={(e) => setSetting({ extra: e.target.value })}
            placeholder="added to every prompt, e.g. wearing a green velvet doublet"
          />
        </div>
      </div>

      {/* ── cue test ─────────────────────────────────────────────── */}
      <CueTest />

      {/* ── volume ───────────────────────────────────────────────── */}
      <div className="rf-wood p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="rf-ink text-2xl" style={{ color: GOLD }}>Selfies over time</h2>
          <div className="rf-chips ml-auto">
            {[['hourly', 'By hour'], ['daily', 'By day']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => { buzz(8); setMode(v); }} className={`rf-chip${mode === v ? ' is-on' : ''}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <VolumeBars rows={ordered} mode={mode} />
        <p className="rf-ink mt-2" style={{ color: 'rgba(247,228,187,.5)', fontSize: '.78rem' }}>
          {mode === 'hourly' ? 'Last 48 hours' : 'Last 60 days'} &middot; a red bar means more than a quarter of that
          bucket failed &middot; hover for the detail
        </p>
      </div>

      {/* ── per-model economics ──────────────────────────────────── */}
      {log.stats?.length > 0 && (
        <div className="rf-wood p-5">
          <h2 className="rf-ink text-2xl mb-3" style={{ color: GOLD }}>By painter</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {log.stats.map((s) => (
              <div key={s.model_id} className="rounded-2xl p-3" style={{ background: 'rgba(0,0,0,.28)' }}>
                <div className="rf-ink" style={{ color: GOLD, fontSize: '1.05rem' }}>{s.model_label || s.model_id}</div>
                <div className="rf-ink mt-1 leading-relaxed" style={{ ...dim, fontSize: '.86rem' }}>
                  {s.runs} runs &middot; {s.failed} failed<br />
                  avg <b>{secs(s.avg_ms)}</b> &middot; slowest {secs(s.max_ms)}<br />
                  avg {usd(s.avg_cost_usd)} &middot; total ${Number(s.total_cost_usd || 0).toFixed(2)}<br />
                  likeness {s.avg_likeness ?? '-'} / 5
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── the run table ────────────────────────────────────────── */}
      <div className="rf-wood p-4 sm:p-5">
        <h2 className="rf-ink text-2xl mb-3" style={{ color: GOLD }}>Every run</h2>
        <div className="overflow-x-auto rf-scroll">
          <table className="w-full text-sm" style={{ color: '#f7e4bb' }}>
            <thead>
              <tr style={{ color: 'rgba(247,228,187,.55)' }}>
                {['', 'When', 'Who', 'Scene', 'Style', 'Painter', 'People', 'Consent', 'Frames', 'Sent',
                  'First paint', 'Generate', 'Total', 'Tokens in', 'Tokens out', 'Cost', 'Score', 'Status']
                  .map((h) => <th key={h} className={th} style={{ fontSize: '.72rem' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {log.runs.map((r) => {
                const u = r.usage_raw || {};
                const open = openRow === r.job_id;
                const src = sources[r.job_id];
                return [
                  <tr key={r.job_id} className="border-t" style={{ borderColor: 'rgba(0,0,0,.35)' }}>
                    <td className={td}>
                      <button type="button" onClick={() => toggleRow(r.job_id)} aria-label="Details" style={{ color: GOLD }}>
                        {open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      </button>
                    </td>
                    <td className={td} style={dim}>{new Date(r.created_at).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                    <td className={`${td} max-w-[12rem] truncate`} style={dim} title={r.user_email || ''}>{r.user_email || '-'}</td>
                    <td className={td} style={dim}>{r.scene}</td>
                    <td className={td} style={dim}>{r.likeness}{r.banner ? ' +banner' : ''}</td>
                    <td className={td} style={dim}>{r.model_label || r.model_id}</td>
                    <td className={td} style={{ color: r.is_group ? GOLD : 'rgba(247,228,187,.72)' }}>
                      {r.is_group ? `${r.people_count} \u{1F465}` : '1'}
                    </td>
                    <td className={td} style={{ color: r.consent_at ? 'rgba(247,228,187,.72)' : '#ef8a6a' }}>
                      {r.consent_at ? (r.consent_version || 'yes') : 'none'}
                    </td>
                    <td className={td} style={dim}>{r.frame_count}</td>
                    <td className={td} style={dim}>{kb(r.payload_bytes)}</td>
                    <td className={td} style={dim}>{secs(r.first_partial_ms)}</td>
                    <td className={td} style={{ color: r.generation_ms > 90000 ? '#f0b429' : 'rgba(247,228,187,.72)' }}>{secs(r.generation_ms)}</td>
                    <td className={td} style={dim}>{secs(r.total_ms)}</td>
                    <td className={td} style={dim}>{u.input_tokens ?? '-'}</td>
                    <td className={td} style={dim}>{u.output_tokens ?? '-'}</td>
                    <td className={td} style={{ color: GOLD }}>{usd(r.cost_usd)}</td>
                    <td className={td} style={dim}>{r.score_likeness ? '★'.repeat(r.score_likeness) : '-'}</td>
                    <td className={td} style={{ color: r.status === 'error' ? '#ef8a6a' : 'rgba(247,228,187,.72)' }}>{r.status}</td>
                  </tr>,

                  open ? (
                    <tr key={`${r.job_id}-d`}>
                      <td colSpan={18} className="p-4" style={{ background: 'rgba(0,0,0,.3)' }}>
                        <div className="grid lg:grid-cols-[auto,1fr] gap-5">
                          <div className="space-y-3">
                            {/* ⚠️ The evidence: what went in, what came back mid-flight,
                                and what came out. This is the only place the three sit
                                side by side, and it is what makes a bad likeness
                                diagnosable rather than just disappointing. */}
                            <div>
                              <p className="rf-ink mb-1" style={{ color: GOLD, fontSize: '.86rem' }}>Sent to the painter</p>
                              <div className="flex gap-2">
                                {src?.error && <span className="text-xs text-red-400">{src.error}</span>}
                                {!src && <span className="rf-ink" style={{ ...dim, fontSize: '.8rem' }}>Reading...</span>}
                                {(src?.sources || []).map((f) => (
                                  <img key={f.path} src={f.url} alt="reference frame" style={{ width: 78, aspectRatio: '3/4', objectFit: 'cover', borderRadius: 8 }} />
                                ))}
                                {src && !src.error && !(src.sources || []).length && (
                                  <span className="rf-ink" style={{ ...dim, fontSize: '.8rem' }}>No frames recorded for this run.</span>
                                )}
                              </div>
                            </div>

                            {(src?.partials || []).length > 0 && (
                              <div>
                                <p className="rf-ink mb-1" style={{ color: GOLD, fontSize: '.86rem' }}>Painted, pass by pass</p>
                                <div className="flex gap-2">
                                  {src.partials.map((pp) => (
                                    <div key={pp.i}>
                                      <img src={pp.url} alt={`pass ${pp.i + 1}`} style={{ width: 78, aspectRatio: '4/5', objectFit: 'cover', borderRadius: 8 }} />
                                      <span className="rf-ink block text-center" style={{ ...dim, fontSize: '.68rem' }}>{secs(pp.ms)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {r.image_url && (
                              <div>
                                <p className="rf-ink mb-1" style={{ color: GOLD, fontSize: '.86rem' }}>Finished</p>
                                <img src={r.image_url} alt="portrait" style={{ width: 168, aspectRatio: '4/5', objectFit: 'cover', borderRadius: 10 }} />
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 space-y-3">
                            {r.error && <p className="text-sm text-red-400 break-words"><b>Error:</b> {r.error}{r.stage ? ` (stage: ${r.stage})` : ''}</p>}

                            {r.cost_basis && (
                              <div>
                                <p className="rf-ink" style={{ color: GOLD, fontSize: '.86rem' }}>How the cost was worked out</p>
                                <p className="rf-ink" style={{ ...dim, fontSize: '.82rem' }}>{r.cost_basis}</p>
                              </div>
                            )}

                            {r.attempts && (
                              <div>
                                <p className="rf-ink" style={{ color: GOLD, fontSize: '.86rem' }}>Attempts</p>
                                <p className="rf-ink" style={{ ...dim, fontSize: '.82rem' }}>
                                  {r.attempts.map((a) => `${a.model_id}: ${a.ok ? 'ok' : 'failed'} in ${secs(a.ms)}${a.error ? ` - ${a.error}` : ''}`).join(' | ')}
                                </p>
                              </div>
                            )}

                            <div>
                              <p className="rf-ink" style={{ color: GOLD, fontSize: '.86rem' }}>Prompt sent</p>
                              <pre className="whitespace-pre-wrap break-words" style={{ ...dim, fontSize: '.76rem' }}>{r.prompt}</pre>
                            </div>

                            <details>
                              <summary className="rf-ink cursor-pointer" style={{ color: GOLD, fontSize: '.86rem' }}>Raw usage &amp; device</summary>
                              <pre className="whitespace-pre-wrap break-words mt-1" style={{ ...dim, fontSize: '.72rem' }}>
                                {JSON.stringify({ usage: r.usage_raw, client: r.client_meta }, null, 2)}
                              </pre>
                            </details>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>

        {!log.runs.length && !busy && (
          <p className="rf-ink py-6 text-center" style={{ color: 'rgba(247,228,187,.5)' }}>
            <Sparkles size={18} className="inline mr-2" />Nothing painted yet.
          </p>
        )}
      </div>
    </div>
  );
}
